import { Link, useNavigate } from 'react-router-dom';
//import { RiShoppingCartLine } from "react-icons/ri";
import { IoPricetags } from "react-icons/io5";
import { FaShippingFast } from "react-icons/fa";
import { BiSolidTrash } from "react-icons/bi";
import { useI18n } from '@/i18n';
import { useCartStore } from '@/store/cartStore';
import { useDeliverySettingsStore } from '@/store/deliverySettingsStore';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Minus,
  Plus,
  //Trash2,
  ArrowRight,
  Package,
  Tag
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export function Cart() {
  const { t, currentLang } = useI18n();
  const navigate = useNavigate();
  const {
    items,
    updateQuantity,
    removeItem,
    getTotalPrice,
    getDeliveryFee,
    getFinalTotal,
    clearCart,
  } = useCartStore();
  const deliveryConfig = useDeliverySettingsStore((state) => state.config);

  return (
    <div className="min-h-[100dvh] bg-gray-50/50 font-sans">
      <div className="animate-fade-in pb-28 lg:pb-10">
        {/* Premium Header — Gold Standard */}
        <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-gray-100/50 shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-all">
          <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-center">
            <h1 className="text-lg font-black tracking-tight text-center">{t('cart.title')}</h1>
          </div>
        </header>

        <div className="container mx-auto px-4 max-w-lg mt-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center py-20 text-center">
              <div className="w-56 h-56 bg-none rounded-full flex items-center justify-center mb-0">
                <img src="/images/Cart Empty.png" alt="Empty Cart" className="w-48 h-48 object-contain" />
              </div>
              {/*h3 className="text-2xl text-black font-bold">
                {t('cart.empty')}
              </h3>*/}
              <p className="text-black text-sm font-medium mt-1 max-w-[240px]">
                {t('cart.empty_message')}
              </p>
              <Link to="/products" className="mt-4">
                <Button size="lg" className="rounded-full px-8 font-bold shadow-lg shadow-primary/20">
                  {t('btn.continue_shopping')}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Cart Items */}
              <div className="space-y-3">
                {items.map((item) => (
                  <div key={item.productId} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="p-3 flex gap-3">
                      {/* Product Image */}
                      <Link
                        to={`/product/${item.productId}`}
                        className="w-[72px] h-[72px] shrink-0"
                      >
                        <img
                          src={item.image || '/placeholder-product.png'}
                          alt={currentLang === 'bn' ? item.nameBn : item.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      </Link>

                      {/* Product Info + Controls */}
                      <div className="flex-1 min-w-0 flex flex-col justify-between">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 mt-1">
                            <Link to={`/product/${item.productId}`}>
                              <h3 className="font-bold text-sm text-black leading-tight truncate hover:text-primary transition-colors">
                                {currentLang === 'bn' ? item.nameBn : item.name}
                              </h3>
                            </Link>
                            <p className="text-[11px] text-gray-800 font-medium mt-0.5">
                              {item.weight}g
                            </p>
                          </div>
                          <button
                            onClick={() => removeItem(item.productId)}
                            className="shrink-0 text-red-500 hover:text-red-500 transition-colors p-2"
                          >
                            <BiSolidTrash className="h-3.5 w-3.5" />
                          </button>
                        </div>

                        {/* Price + Quantity Row */}
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-black font-black text-sm">
                            ৳{(item.quantity * item.price).toFixed(0)}
                          </span>
                          <div className="flex items-center gap-0 bg-gray-50 rounded-full border border-gray-100">
                            <button
                              className="h-7 w-7 flex items-center justify-center rounded-full text-black hover:text-black transition-colors disabled:opacity-30"
                              onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="w-7 text-center text-xs font-black text-black">{item.quantity}</span>
                            <button
                              className="h-7 w-7 flex items-center justify-center rounded-full text-black hover:text-black transition-colors disabled:opacity-30"
                              onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                              disabled={item.quantity >= item.stock}
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Clear Cart — styled confirmation */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    className="w-full text-center text-xs text-red-600 font-medium py-2 hover:text-red-600 transition-colors active:scale-95"
                  >
                    {currentLang === 'bn' ? 'কার্ট খালি করুন' : 'Clear Cart'}
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-xl max-w-xs mx-auto">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-base font-black text-center">
                      {currentLang === 'bn' ? 'কার্ট খালি করবেন?' : 'Clear Cart?'}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-center text-sm text-black -mx-1">
                      {currentLang === 'bn'
                        ? 'সব পণ্য কার্ট থেকে সরানো হবে'
                        : 'All items will be removed from your cart'}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter className="flex-row gap-2 sm:justify-center">
                    <AlertDialogCancel className="flex-1 rounded-xl font-bold h-10 mt-0 hover:bg-transparent hover:text-black">
                      {currentLang === 'bn' ? 'না' : 'Cancel'}
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={clearCart}
                      className="flex-1 rounded-xl font-bold h-10 bg-red-600 hover:bg-red-600 text-white"
                    >
                      {currentLang === 'bn' ? 'হ্যাঁ, খালি করুন' : 'Yes, Clear'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Order Summary */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <h2 className="text-base font-black text-black mb-4">
                  {t('checkout.order_summary')}
                </h2>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-black flex items-center gap-2 font-medium">
                      <IoPricetags className="h-4 w-4" />
                      {t('cart.product_cost')}
                    </span>
                    <span className="font-medium text-black ">৳{getTotalPrice().toFixed(0)}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-black flex items-center gap-2 font-medium">
                      <FaShippingFast className="h-4 w-4" />
                      {t('cart.delivery_fee')}
                    </span>
                    <span className="font-bold">
                      {getDeliveryFee() === 0 ? (
                        <span className="text-green-600">
                          {currentLang === 'bn' ? 'ফ্রি' : 'Free'}
                        </span>
                      ) : (
                        <span className="text-black font-medium">৳{getDeliveryFee().toFixed(0)}</span>
                      )}
                    </span>
                  </div>

                  <div className={`text-xs p-2.5 rounded-xl font-medium text-center ${
                    getDeliveryFee() === 0 
                      ? 'text-green-600 bg-green-50' 
                      : 'text-gray-500 bg-gray-100'
                  }`}>
                    {currentLang === 'bn'
                      ? `🎉 ৳${deliveryConfig.freeDeliveryThreshold}+ অর্ডারে ফ্রি ডেলিভারি!`
                      : `🎉 Free delivery on orders over ৳${deliveryConfig.freeDeliveryThreshold}!`}
                  </div>

                  <Separator className="my-1" />

                  <div className="flex justify-between text-base">
                    <span className="font-black text-black">{t('cart.total')}</span>
                    <span className="font-black text-black">
                      ৳{getFinalTotal().toFixed(0)}
                    </span>
                  </div>
                </div>

                <div className="mt-5 flex flex-col gap-2">
                  <Button
                    size="lg"
                    className="w-full text-white rounded-xl font-bold text-sm h-12 shadow-lg shadow-primary/20 active:scale-[0.98] transition-all"
                    onClick={() => navigate('/checkout')}
                  >
                    {t('cart.proceed')}
                    <ArrowRight className="h-4 w-4" strokeWidth={3} />
                  </Button>
                  <Link to="/products">
                    <Button variant="ghost" className="w-full text-primary font-medium text-xs h-9 hover:bg-transparent hover:text-primary">
                      {t('btn.continue_shopping')}
                    </Button>
                  </Link>
                </div>

                {/* Trust Badges */}
                <div className="mt-3 pt-3 bg-white">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <Package className="h-4 w-4 mx-auto mb-1 text-primary" />
                      <p className="text-[10px] text-gray-900 font-medium">
                        {currentLang === 'bn' ? 'সুরক্ষিত প্যাকেজিং' : 'Secure Packaging'}
                      </p>
                    </div>
                    <div>
                      <Tag className="h-4 w-4 mx-auto mb-1 text-primary" />
                      <p className="text-[10px] text-gray-900 font-medium">
                        {currentLang === 'bn' ? 'সেরা দাম' : 'Best Price'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
